import type {
  ListingPersonalization,
  PersonalizationQuestion,
} from "../types/listing";

// Etsy field constraints (Open API v3, multi-personalization GA 2026-05).
export const PERSONALIZATION_LIMITS = {
  maxQuestions: 5,
  questionTextMax: 45,
  instructionsMax: 120,
  charCountMin: 1,
  charCountMax: 1024,
  dropdownOptionsMin: 1,
  dropdownOptionsMax: 30,
  dropdownLabelMax: 20,
  uploadFilesMin: 1,
  uploadFilesMax: 10,
  labeledUploadFilesMin: 2,
  labeledLabelMax: 45,
} as const;

/**
 * Normalize a personalization value into the new multi-question shape.
 * Handles three inputs:
 *  - null/undefined  -> { enabled: false, questions: [] }
 *  - new shape       -> returned as-is (questions kept)
 *  - legacy shape    -> { enabled, instructions, maxLength } converted to a single text_input question
 */
export function normalizePersonalization(
  value: ListingPersonalization | null | undefined
): { enabled: boolean; questions: PersonalizationQuestion[] } {
  if (!value || !value.enabled) {
    return { enabled: false, questions: [] };
  }

  // Already in the new shape.
  if (Array.isArray(value.questions) && value.questions.length > 0) {
    return {
      enabled: true,
      questions: value.questions.slice(0, PERSONALIZATION_LIMITS.maxQuestions),
    };
  }

  // Legacy shape -> single text_input question.
  const question: PersonalizationQuestion = {
    question_type: "text_input",
    question_text: "Personalization",
    required: true,
    max_allowed_characters: clampCharCount(value.maxLength),
  };
  const instructions = (value.instructions ?? "").trim();
  if (instructions) {
    question.instructions = instructions.slice(
      0,
      PERSONALIZATION_LIMITS.instructionsMax
    );
  }
  return { enabled: true, questions: [question] };
}

function clampCharCount(maxLength: number | undefined): number {
  const n = Number(maxLength);
  if (!Number.isFinite(n) || n <= 0) return 50;
  return Math.min(
    PERSONALIZATION_LIMITS.charCountMax,
    Math.max(PERSONALIZATION_LIMITS.charCountMin, Math.round(n))
  );
}

/**
 * Parse Etsy's personalization read payload into our question shape.
 * Accepts either the listing's `personalization` object (with a
 * `personalization_questions` array) or a bare questions array.
 */
/** Etsy metinleri HTML kodlu doner (&#39; &quot; &amp;): karsilastirma ve geri yazmadan once cozulur */
const decodeEtsy = (v: unknown) => String(v ?? "")
  .replace(/&#(\d+);/g, (_m, n: string) => String.fromCodePoint(Number(n)))
  .replace(/&#x([0-9a-f]+);/gi, (_m, n: string) => String.fromCodePoint(parseInt(n, 16)))
  .replace(/&quot;/g, '"').replace(/&apos;/g, "'").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&amp;/g, "&");

export function parseEtsyPersonalizationQuestions(
  raw: unknown
): PersonalizationQuestion[] {
  const arr = Array.isArray(raw)
    ? raw
    : (raw as { personalization_questions?: unknown[] } | null)?.personalization_questions;
  if (!Array.isArray(arr)) return [];
  return arr
    .map((q): PersonalizationQuestion | null => {
      const item = q as Record<string, unknown>;
      const type = item.question_type as PersonalizationQuestion["question_type"];
      if (!type) return null;
      const out: PersonalizationQuestion = {
        question_type: type,
        question_text: decodeEtsy(item.question_text),
        required: !!item.required,
      };
      if (item.instructions) out.instructions = decodeEtsy(item.instructions);
      if (item.max_allowed_characters != null) out.max_allowed_characters = Number(item.max_allowed_characters);
      if (item.max_allowed_files != null) out.max_allowed_files = Number(item.max_allowed_files);
      if (Array.isArray(item.options)) {
        out.options = item.options.map((o) => ({ label: decodeEtsy((o as { label?: unknown }).label) }));
      }
      // Ek ucret Etsy'den Money olarak doner; geri yazarken gonderilmezse Etsy reddeder
      const ap = item.add_on_price as { amount?: number; divisor?: number } | number | null | undefined;
      const addOn = typeof ap === "number" ? ap : ap && typeof ap.amount === "number" ? ap.amount / (ap.divisor || 100) : null;
      if (addOn != null && addOn > 0) out.add_on_price = addOn;
      return out;
    })
    .filter((q): q is PersonalizationQuestion => q !== null)
    .slice(0, PERSONALIZATION_LIMITS.maxQuestions);
}

/**
 * Validate a list of personalization questions against Etsy constraints.
 * Returns an array of human-readable error strings (empty = valid).
 */
export function validatePersonalizationQuestions(
  questions: PersonalizationQuestion[]
): string[] {
  const errors: string[] = [];
  if (questions.length === 0) return errors;
  if (questions.length > PERSONALIZATION_LIMITS.maxQuestions) {
    errors.push(`En fazla ${PERSONALIZATION_LIMITS.maxQuestions} soru olabilir.`);
  }

  let uploadCount = 0;
  questions.forEach((q, i) => {
    const n = i + 1;
    const text = (q.question_text ?? "").trim();
    if (text.length < 1 || text.length > PERSONALIZATION_LIMITS.questionTextMax) {
      errors.push(`Soru ${n}: başlık 1-${PERSONALIZATION_LIMITS.questionTextMax} karakter olmalı.`);
    }

    if (q.question_type === "text_input") {
      const c = Number(q.max_allowed_characters);
      if (
        !Number.isFinite(c) ||
        c < PERSONALIZATION_LIMITS.charCountMin ||
        c > PERSONALIZATION_LIMITS.charCountMax
      ) {
        errors.push(`Soru ${n}: maksimum karakter ${PERSONALIZATION_LIMITS.charCountMin}-${PERSONALIZATION_LIMITS.charCountMax} arası olmalı.`);
      }
      if ((q.instructions ?? "").length > PERSONALIZATION_LIMITS.instructionsMax) {
        errors.push(`Soru ${n}: talimatlar en fazla ${PERSONALIZATION_LIMITS.instructionsMax} karakter.`);
      }
    } else if (q.question_type === "dropdown") {
      if (q.instructions) {
        errors.push(`Soru ${n}: açılır listede talimat kullanılamaz.`);
      }
      const opts = (q.options ?? []).filter((o) => (o.label ?? "").trim());
      if (
        opts.length < PERSONALIZATION_LIMITS.dropdownOptionsMin ||
        opts.length > PERSONALIZATION_LIMITS.dropdownOptionsMax
      ) {
        errors.push(`Soru ${n}: ${PERSONALIZATION_LIMITS.dropdownOptionsMin}-${PERSONALIZATION_LIMITS.dropdownOptionsMax} seçenek gerekli.`);
      }
      if (opts.some((o) => o.label.trim().length > PERSONALIZATION_LIMITS.dropdownLabelMax)) {
        errors.push(`Soru ${n}: seçenek etiketleri en fazla ${PERSONALIZATION_LIMITS.dropdownLabelMax} karakter.`);
      }
    } else {
      // upload types
      uploadCount += 1;
      const f = Number(q.max_allowed_files);
      const min =
        q.question_type === "labeled_upload"
          ? PERSONALIZATION_LIMITS.labeledUploadFilesMin
          : PERSONALIZATION_LIMITS.uploadFilesMin;
      if (!Number.isFinite(f) || f < min || f > PERSONALIZATION_LIMITS.uploadFilesMax) {
        errors.push(`Soru ${n}: dosya sayısı ${min}-${PERSONALIZATION_LIMITS.uploadFilesMax} arası olmalı.`);
      }
      if (q.question_type === "labeled_upload") {
        const opts = (q.options ?? []).filter((o) => (o.label ?? "").trim());
        if (opts.length !== Number(q.max_allowed_files)) {
          errors.push(`Soru ${n}: etiketli yüklemede etiket sayısı dosya sayısına eşit olmalı.`);
        }
      }
    }
  });

  if (uploadCount > 1) {
    errors.push("Liste başına yalnızca 1 dosya yükleme sorusu olabilir.");
  }

  return errors;
}

/** Etsy "all_caps" kurali: 3+ buyuk harfle baslayan birden cok kelime reddedilir (EMMA, DIABETIC). Bas harf kalir. */
export function softenCaps(text: string): string {
  return text.replace(/\b([A-Z])([A-Z]{2,}[A-Za-z]*)\b/g, (_, a: string, b: string) => a + b.toLowerCase());
}

/**
 * Build the request body for Etsy's personalization write endpoint.
 * Strips empty/undefined fields per question type.
 */
export function buildEtsyPersonalizationPayload(
  questions: PersonalizationQuestion[]
): { personalization_questions: Record<string, unknown>[] } {
  const personalization_questions = questions.slice(0, PERSONALIZATION_LIMITS.maxQuestions).map((q) => {
    const out: Record<string, unknown> = {
      question_type: q.question_type,
      question_text: (q.question_text ?? "").trim().slice(0, PERSONALIZATION_LIMITS.questionTextMax),
      required: !!q.required,
    };
    if (q.question_type === "text_input") {
      out.max_allowed_characters = clampCharCount(q.max_allowed_characters);
      const instr = softenCaps((q.instructions ?? "").trim());
      if (instr) out.instructions = instr.slice(0, PERSONALIZATION_LIMITS.instructionsMax);
      // Ek ucret: yalniz istege bagli metin sorusu (Etsy add-on pricing, 0.20-500 USD)
      const addOn = Number(q.add_on_price);
      if (!q.required && Number.isFinite(addOn) && addOn >= 0.2) out.add_on_price = Math.round(Math.min(500, addOn) * 100) / 100;
    } else if (q.question_type === "dropdown") {
      out.options = (q.options ?? [])
        .map((o) => ({ label: (o.label ?? "").trim() }))
        .filter((o) => o.label);
    } else {
      out.max_allowed_files = Number(q.max_allowed_files) || PERSONALIZATION_LIMITS.uploadFilesMin;
      const instr = (q.instructions ?? "").trim();
      if (instr) out.instructions = instr.slice(0, PERSONALIZATION_LIMITS.instructionsMax);
      if (q.question_type === "labeled_upload") {
        out.options = (q.options ?? [])
          .map((o) => ({ label: (o.label ?? "").trim() }))
          .filter((o) => o.label);
      }
    }
    return out;
  });
  return { personalization_questions };
}
